import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { friendlyError, reportError } from "../lib/errorReporter";
import Layout from "../components/Layout";
import { fmtDate } from "../lib/format";

const EMPTY_FORM = { name: "", email: "", phone: "", company: "", address: "" };

export default function CustomersList() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/customers", { params: q ? { q } : {} });
      setItems(r.data || []);
    } catch (err) {
      // Non-fatal: leave last known data on screen so the user isn't dropped
      // into an empty state on a flaky network request.
      reportError("customersList.load", err);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onOrg = () => load();
    window.addEventListener("quowada:orgChanged", onOrg);
    return () => window.removeEventListener("quowada:orgChanged", onOrg);
  }, [load]);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      try {
        await api.post("/customers", form);
        setShowCreate(false);
        setForm(EMPTY_FORM);
        load();
      } catch (err) {
        reportError("customersList.create", err);
        setError(friendlyError(err, t("common.error_generic")));
      }
    },
    [form, load, t],
  );

  return (
    <Layout
      title={t("customers.title")}
      actions={
        <button className="qw-btn primary" onClick={() => setShowCreate(true)} data-testid="btn-new-customer">
          + {t("customers.new")}
        </button>
      }
    >
      <div className="h1">{t("customers.title")}</div>
      <div style={{ height: 16 }} />
      <div className="hstack" style={{ marginBottom: 12 }}>
        <input
          className="qw-input"
          placeholder={t("common.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 280 }}
          data-testid="customers-search"
        />
      </div>
      {items.length === 0 ? (
        <div className="qw-card qw-empty">{t("customers.empty")}</div>
      ) : (
        <div className="qw-table-wrap" tabIndex={0} role="region" aria-label={t("customers.title")}>
        <table className="qw-table" data-testid="customers-table">
          <thead>
            <tr>
              <th>{t("customers.fields.name")}</th>
              <th>{t("customers.fields.company")}</th>
              <th>{t("customers.fields.email")}</th>
              <th>{t("customers.fields.phone")}</th>
              <th>{t("quotations.created")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td style={{ fontWeight: 600 }}>{c.name}</td>
                <td>{c.company || "—"}</td>
                <td>{c.email || "—"}</td>
                <td>{c.phone || "—"}</td>
                <td>{fmtDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {showCreate && (
        <div className="qw-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="qw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="h">{t("customers.new")}</div>
            <form onSubmit={submit}>
              <div className="qw-field">
                <label>{t("customers.fields.name")}</label>
                <input
                  className="qw-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="cust-name"
                />
              </div>
              <div className="qw-field">
                <label>{t("customers.fields.company")}</label>
                <input className="qw-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div className="qw-field">
                <label>{t("customers.fields.email")}</label>
                <input className="qw-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="qw-field">
                <label>{t("customers.fields.phone")}</label>
                <input className="qw-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="qw-field">
                <label>{t("customers.fields.address")}</label>
                <input className="qw-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              {error && <div className="qw-error">{error}</div>}
              <div className="footer">
                <button type="button" className="qw-btn ghost" onClick={() => setShowCreate(false)}>
                  {t("common.cancel")}
                </button>
                <button type="submit" className="qw-btn primary" data-testid="cust-create">
                  {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
